/**
 * PHONE INPUT IFRAME COMPONENT
 * Version du PhoneInput qui utilise RadixSelect avec portal={false}
 * Spécialement conçu pour les formulaires en iframe pour éviter les problèmes de positionnement
 */

import { forwardRef, useState, useCallback } from 'react';
import type { InputHTMLAttributes, ChangeEvent } from 'react';
import { cn } from '../../lib/utils';
import { RadixSelect } from './radix-select';
import type { RadixSelectOption } from './radix-select';
import {
  COUNTRY_CODES,
  parsePhoneNumber,
  formatPhoneNumber,
  type CountryCode,
} from './phone-input';

export interface PhoneInputIframeProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  /** Valeur complète du téléphone (avec indicatif) */
  value?: string;
  /** Callback quand la valeur change (retourne le numéro complet avec indicatif) */
  onChange?: (value: string) => void;
  /** Code pays par défaut */
  defaultCountryCode?: CountryCode;
}

export const PhoneInputIframe = forwardRef<HTMLInputElement, PhoneInputIframeProps>(
  ({ className, value = '', onChange, defaultCountryCode = 'FR', disabled, ...props }, ref) => {
    // Parser la valeur initiale
    const parsed = parsePhoneNumber(value);
    const [countryCode, setCountryCode] = useState<CountryCode>(
      value ? parsed.countryCode : defaultCountryCode
    );
    const [localNumber, setLocalNumber] = useState(parsed.localNumber);

    // Mettre à jour quand la valeur externe change
    const externalParsed = parsePhoneNumber(value);
    if (
      value &&
      (externalParsed.countryCode !== countryCode || externalParsed.localNumber !== localNumber)
    ) {
      // Sync uniquement si la valeur vient de l'extérieur
      if (formatPhoneNumber(countryCode, localNumber) !== value) {
        setCountryCode(externalParsed.countryCode);
        setLocalNumber(externalParsed.localNumber);
      }
    }

    const handleCountryChange = useCallback(
      (newCode: string) => {
        const code = newCode as CountryCode;
        setCountryCode(code);
        onChange?.(formatPhoneNumber(code, localNumber));
      },
      [localNumber, onChange]
    );

    const handleNumberChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const newNumber = e.target.value;
        setLocalNumber(newNumber);
        onChange?.(formatPhoneNumber(countryCode, newNumber));
      },
      [countryCode, onChange]
    );

    // Préparer les options pour RadixSelect
    const countryOptions: RadixSelectOption[] = COUNTRY_CODES.map((country) => ({
      value: country.code,
      label: `${country.flag} ${country.dial}`,
    }));

    return (
      <div className={cn('flex w-full min-w-0 gap-2', className)}>
        {/* Sélecteur de pays avec RadixSelect */}
        <div className="w-32 flex-shrink-0">
          <RadixSelect
            value={countryCode}
            onChange={handleCountryChange}
            options={countryOptions}
            disabled={disabled}
            disablePortal={true}
          />
        </div>

        {/* Input numéro */}
        <input
          ref={ref}
          type="tel"
          value={localNumber}
          onChange={handleNumberChange}
          disabled={disabled}
          placeholder={props.placeholder || '6 12 34 56 78'}
          className={cn(
            'h-9 min-w-0 flex-1 rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none transition-colors',
            'placeholder:text-zinc-400',
            'focus-visible:border-zinc-900 focus-visible:ring-2 focus-visible:ring-zinc-900',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
          {...props}
        />
      </div>
    );
  }
);

PhoneInputIframe.displayName = 'PhoneInputIframe';
